import { appGridApiAtom } from "@/components/AppGrid/AppGrid";
import { Icons } from "@/components/Icons/Icons";
import {
  Tag,
  getAllPalettes,
  getPaletteByColor,
  getPaletteById,
  getRandomPaletteId,
} from "@/models/tag";
import { Entity, EntityRecord, PartialEntityRecord } from "@/orm/types";
import { getShortId } from "@/utils/id";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Button,
  ColorPicker,
  ColorSwatch,
  Divider,
  Group,
  Popover,
  Stack,
  TextInput,
} from "@mantine/core";
import { useListState } from "@mantine/hooks";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useAsyncCallback } from "react-async-hook";
import { FaFillDrip } from "react-icons/fa";
import { MdDragIndicator } from "react-icons/md";

export const EditTagList = <T extends EntityRecord = EntityRecord>({
  onUpdateComplete,
  entity,
  initTags,
  resourceProp,
  label = "Status",
}: {
  onUpdateComplete?: () => void;
  entity: Entity<T>;
  resourceProp: string;
  initTags: Tag[];
  label?: string;
}) => {
  const gridApi = useAtomValue(appGridApiAtom);
  const { loading: isUpdating, execute: update } = useAsyncCallback(
    entity.update,
  );

  const [tags, handlers] = useListState<Tag>(initTags);

  const onAddTag = () => {
    handlers.append({
      id: getShortId(),
      label: `New ${label}`,
      paletteId: getRandomPaletteId(),
    });
  };

  return (
    <DragDropContext
      onDragEnd={({ destination, source }) =>
        handlers.reorder({ from: source.index, to: destination?.index || 0 })
      }
    >
      <Stack gap={"xs"}>
        <Droppable droppableId="dnd-list" direction="vertical">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {tags.map((tag, index) => (
                <Draggable key={tag.id} index={index} draggableId={tag.id}>
                  {(provided) => (
                    <Group
                      gap={0}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <div {...provided.dragHandleProps}>
                        <MdDragIndicator
                          style={{
                            position: "relative",
                            left: "-8px",
                            top: "2px",
                            cursor: "grab",
                          }}
                        />
                      </div>
                      <EditTag
                        tag={tag}
                        setTag={(tag: Tag) => {
                          handlers.setItem(index, tag);
                        }}
                        remove={() => {
                          handlers.remove(index);
                        }}
                      />
                    </Group>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <Stack gap={"xs"}>
          <Button
            size="sm"
            fullWidth
            variant="outline"
            color="gray"
            onClick={onAddTag}
          >
            Add {label}
          </Button>
          <Divider />
          <Button
            fullWidth
            onClick={async () => {
              await update({
                [resourceProp]: tags,
              } as PartialEntityRecord<T>);
              gridApi?.onSortChanged();
              onUpdateComplete?.();
            }}
            loading={isUpdating}
          >
            Apply Changes
          </Button>
        </Stack>
      </Stack>
    </DragDropContext>
  );
};

const EditTag = ({
  tag,
  setTag,
  remove,
}: {
  tag: Tag;
  setTag: (category: Tag) => void;
  remove: () => void;
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const palette = getPaletteById(tag.paletteId);

  return (
    <Group key={tag.id} gap={"xs"} flex={1} pb={4}>
      <Popover
        trapFocus
        width={240}
        position="bottom"
        withArrow
        shadow="md"
        withinPortal={false}
        opened={showColorPicker}
      >
        <Popover.Target>
          <ColorSwatch
            color={getPaletteById(tag.paletteId)?.backgroundColor || "#bada55"}
            radius={4}
            onClick={() => setShowColorPicker(true)}
          >
            <FaFillDrip color={palette?.fontColor} />
          </ColorSwatch>
        </Popover.Target>
        <Popover.Dropdown>
          <ColorPicker
            format="hex"
            value={"#009790"}
            onChange={() => {}}
            withPicker={false}
            fullWidth
            swatches={[...getAllPalettes()]
              .slice(1, 99)
              .map((color) => color.backgroundColor)}
            onColorSwatchClick={(color) => {
              setShowColorPicker(false);
              setTag({
                ...tag,
                paletteId: getPaletteByColor(color)?.id || "gray",
              });
            }}
          />
        </Popover.Dropdown>
      </Popover>

      <TextInput
        flex={1}
        value={tag.label}
        onChange={(event) => {
          setTag({ ...tag, label: event.currentTarget.value });
        }}
        size="xs"
      />

      <Icons.Delete
        cursor={"pointer"}
        color={"var(--mantine-color-gray-5"}
        onClick={remove}
      />
    </Group>
  );
};
